import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ErrorLogger } from "@byscott-io/issuesbyscott_client";
import { configure as configureMetrics, track as metricsTrack } from "@byscott-io/metricsbyscott-web";
import { storage, configureAuth } from '@byscott-io/corebyscott';
import App from "./App";

// Set up client-side error reporting
if (window.ENV?.ISSUESBYSCOTT_API_URL && window.ENV?.ISSUESBYSCOTT_API_KEY) {
  ErrorLogger.configure({
    apiUrl: window.ENV.ISSUESBYSCOTT_API_URL,
    apiKey: window.ENV.ISSUESBYSCOTT_API_KEY,
    environment: window.ENV.RAILS_ENV || "production",
    enabled: window.ENV.RAILS_ENV === "production",
    commitSha: window.ENV?.COMMIT_SHA,
    onError: (eventName, properties) => metricsTrack(eventName, properties),
  });
  ErrorLogger.setupGlobalHandler();

  // setupGlobalHandler catches window.onerror, which React render errors do NOT
  // reach -- React swallows them and unmounts the tree. Core's ErrorBoundary is
  // what sees those, and it reports through core's own config, so point it here.
  // It also carries the component stack, which window.onerror cannot know.
  configureAuth({
    onError: (error, info) =>
      ErrorLogger.report(error, { componentStack: info?.componentStack }),
  });
}

// Set up metrics tracking (metricsbyscott tracks itself)
if (window.ENV?.METRICSBYSCOTT_API_URL && window.ENV?.METRICSBYSCOTT_API_KEY) {
  configureMetrics({
    apiUrl: window.ENV.METRICSBYSCOTT_API_URL,
    apiKey: window.ENV.METRICSBYSCOTT_API_KEY,
    appName: "metricsbyscott",
    enabled: window.ENV.RAILS_ENV === "production",
    // Seed identity synchronously from cached auth (cookies) so a logged-in
    // user's very first page_view — even on marketing pages — is attributed,
    // without waiting for /me. member_id fills in via MetricsIdentity once /me
    // resolves; flush-backfill stamps any events buffered before then.
    userId: storage.getCurrentUserId() || undefined,
    containerId: storage.getCurrentContainerId() || undefined,
    // Pilot: also autocapture ANONYMOUS clicks (element-chain fallback) so we can
    // see the full un-anchored tail. Named autocapture is on by default.
    autoClickTrackingAnonymous: true,
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      retry: 1,
    },
  },
});

document.addEventListener("DOMContentLoaded", () => {
  const container = document.getElementById("root");
  if (container) {
    const root = createRoot(container);
    root.render(
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </QueryClientProvider>
    );
  }
});
